/**
 * Soft Navigation Helper
 *
 * Chrome's Soft Navigation API (Chrome 139+) provides native detection
 * of single-page application navigations.
 *
 * This helper provides:
 * - Type definitions for Soft Navigation entries
 * - Observer factory for soft navigation events
 * - LCP tracking via "interaction-contentful-paint" (NOT largest-contentful-paint!)
 * - Duration calculation based on network quiet + ICP + long tasks
 * - Layout shift tracking during navigation
 * - Separate reporting with type "softnavigation"
 *
 * IMPORTANT: Chrome uses "interaction-contentful-paint" for soft navigation LCP,
 * NOT "largest-contentful-paint". The standard LCP is finalized on user interaction
 * and only measures the initial hard navigation.
 *
 * Reference: https://developer.chrome.com/docs/web-platform/soft-navigations-experiment
 */

import PerformanceMonitoringService from "@/services/PerformanceMonitoringService";
import {
  getSessionId,
  getUserId,
  getLocationId,
  getEffectiveNetworkType,
  createResourceObserver,
  type ResourceTimingEntry,
} from "./ttiHelpers";
import { shouldIgnoreForTTI } from "./networkIgnoreList";

// ============================================================================
// Types
// ============================================================================

/**
 * Soft Navigation entry from Chrome's Performance API
 * navigationId is unsigned long long per WICG spec
 */
export interface SoftNavigationEntry extends PerformanceEntry {
  navigationId: number; // unsigned long long - pseudo-random number identifying this navigation
  startTime: number; // DOMHighResTimeStamp relative to performance.timeOrigin
  name: string; // URL of the navigation
}

/**
 * Interaction Contentful Paint entry for soft navigation LCP
 * Chrome 139+ uses "interaction-contentful-paint" instead of "largest-contentful-paint"
 * for measuring LCP after soft navigations.
 *
 * Note: standard "largest-contentful-paint" is finalized on user interaction
 * and only measures the initial hard navigation LCP.
 */
export interface InteractionContentfulPaintEntry extends PerformanceEntry {
  navigationId?: number; // Links to the soft navigation
  element?: Element;
  url?: string;
  size?: number;
}

/**
 * Layout Shift entry for tracking visual stability during soft navigation
 */
export interface LayoutShiftEntry extends PerformanceEntry {
  value: number; // Layout shift score
  hadRecentInput: boolean; // Whether shift was caused by user input
  sources?: Array<{
    node?: Node;
    previousRect: DOMRectReadOnly;
    currentRect: DOMRectReadOnly;
  }>;
}

/**
 * Long Task entry for tracking JavaScript blocking time
 */
export interface LongTaskEntry extends PerformanceEntry {
  attribution?: Array<{
    name: string;
    entryType: string;
    startTime: number;
    duration: number;
    containerType?: string;
    containerSrc?: string;
    containerId?: string;
    containerName?: string;
  }>;
}

/**
 * Network request info captured during soft navigation
 * Note: DNS/TCP/SSL timing removed as they're typically 0 due to connection reuse
 */
export interface SoftNavNetworkRequest {
  url: string;
  initiatorType: string; // 'fetch', 'xmlhttprequest', 'script', 'img', 'css', etc.
  startTime: number; // Relative to navigation start
  duration: number; // Total request duration in ms
  transferSize: number; // Bytes transferred
  ttfb?: number; // Time to first byte (most useful timing metric)
}

/**
 * Report data for soft navigation events
 *
 * Note: FCP is NOT tracked for soft navigations because:
 * - FCP is a one-time metric that only fires during initial page load
 * - Chrome does not emit new FCP entries for soft navigations
 * - ICP (Interaction Contentful Paint) is the appropriate metric for soft navigation rendering
 */
export interface SoftNavigationReportData {
  type: "softnavigation";
  navigationId: number;
  url: string;
  fromRoute: string; // Previous route/URL
  toRoute: string; // New route/URL
  startTime: number;
  duration: number; // Calculated: time from startTime to navigation "end"
  // ICP data (Interaction Contentful Paint - soft navigation's equivalent of LCP)
  icp?: number;
  icpElement?: string;
  icpRating?: "good" | "needs-improvement" | "poor";
  // Network activity summary
  networkRequestCount: number;
  lastNetworkActivityTime: number; // Time of last network request completion
  // Detailed network requests during this navigation
  networkRequests: SoftNavNetworkRequest[];
  // Long task metrics
  longTaskCount: number;
  totalBlockingTime: number; // Total time spent in long tasks (>50ms portions)
  lastLongTaskEndTime: number;
  // Layout shift metrics
  cumulativeLayoutShift: number;
  layoutShiftCount: number;
  clsRating?: "good" | "needs-improvement" | "poor";
  effectiveNetworkType: string | undefined;
  sessionId: string | undefined;
  userId: string | undefined;
  locationId: string | null;
  timestamp: string;
}

// ============================================================================
// Constants
// ============================================================================

const DEBUG = false;

const debugLog = (...args: unknown[]) => {
  if (DEBUG) console.log("[SoftNav]", ...args);
};

// ============================================================================
// API Support Detection
// ============================================================================

/**
 * Check if Chrome's Soft Navigation API is supported
 */
export function isSoftNavigationSupported(): boolean {
  if (typeof window === "undefined" || !("PerformanceObserver" in window)) {
    return false;
  }

  try {
    return (
      PerformanceObserver.supportedEntryTypes?.includes("soft-navigation") ??
      false
    );
  } catch {
    return false;
  }
}

// ============================================================================
// Observer Factory
// ============================================================================

/**
 * Create a PerformanceObserver for soft navigation events
 * Only works in Chrome 139+ with Soft Navigation API enabled
 */
export function createSoftNavigationObserver(
  onSoftNavigation: (entry: SoftNavigationEntry) => void
): PerformanceObserver | null {
  if (!isSoftNavigationSupported()) {
    debugLog("Soft Navigation API not supported in this browser");
    return null;
  }

  try {
    const observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        onSoftNavigation(entry as SoftNavigationEntry);
      }
    });

    observer.observe({ type: "soft-navigation", buffered: true });
    debugLog("Soft Navigation observer started");
    return observer;
  } catch (error) {
    debugLog("Failed to create Soft Navigation observer:", error);
    return null;
  }
}

// ============================================================================
// State
// ============================================================================

let softNavObserver: PerformanceObserver | null = null;
let icpObserver: PerformanceObserver | null = null; // interaction-contentful-paint for soft nav LCP
let resourceObserver: PerformanceObserver | null = null;
let longTaskObserver: PerformanceObserver | null = null; // Track JavaScript blocking
let layoutShiftObserver: PerformanceObserver | null = null; // Track visual stability
let initialized = false;

// Track active soft navigation
interface ActiveSoftNavigation {
  entry: SoftNavigationEntry;
  fromRoute: string; // Previous URL before navigation
  entryStartTime: number; // From soft-navigation entry (relative to timeOrigin)
  actualStartTime: number; // performance.now() when we started tracking
  lastResourceEndTime: number;
  networkRequestCount: number;
  networkRequests: SoftNavNetworkRequest[]; // Detailed network request info
  processedUrls: Set<string>; // Track URLs we've already processed to avoid duplicates
  lcp?: number;
  lcpElement?: string;
  lcpReceived: boolean; // Whether we've received LCP for this navigation
  // Long task tracking
  longTaskCount: number;
  totalBlockingTime: number; // Sum of (taskDuration - 50ms) for long tasks
  lastLongTaskEndTime: number;
  // Layout shift tracking
  cumulativeLayoutShift: number;
  layoutShiftCount: number;
  // Timers
  quietTimer: ReturnType<typeof setTimeout> | null;
  lcpWaitTimer: ReturnType<typeof setTimeout> | null; // Timer to wait for LCP
  maxTimer: ReturnType<typeof setTimeout> | null;
  reported: boolean;
  // Track when quiet period started (for click finalization check)
  quietStartTime: number | null;
}

let activeNavigation: ActiveSoftNavigation | null = null;

// Track last URL for fromRoute - updated on both soft and hard navigations
let lastUrl: string = typeof window !== "undefined" ? window.location.href : "";

// Listeners for hard navigation events (to keep lastUrl in sync)
let popstateListener: (() => void) | null = null;
let pageshowListener: ((e: PageTransitionEvent) => void) | null = null;

// Max time to wait for navigation to "end"
const MAX_SOFT_NAV_WAIT_MS = 15000;

// Network quiet period for soft navigation (2 seconds - standard TTI threshold)
const SOFT_NAV_QUIET_MS = 2000;

// Time to wait for LCP after network quiet (3 seconds max)
const LCP_WAIT_MS = 3000;

// Minimum quiet time before click can finalize (500ms)
// Prevents premature finalization if user clicks during active loading
const MIN_QUIET_FOR_USER_INTERACTION_MS = 500;

// User interaction listener references for cleanup
let clickListener: ((e: MouseEvent) => void) | null = null;
let keydownListener: ((e: KeyboardEvent) => void) | null = null;

// Keys that should NOT trigger finalization
// These are modifier keys, function keys, and system keys that don't indicate page interaction
const IGNORED_KEYS = new Set([
  // Modifier keys
  "Control",
  "Shift",
  "Alt",
  "Meta",
  "CapsLock",
  "NumLock",
  "ScrollLock",
  // Function keys
  "F1",
  "F2",
  "F3",
  "F4",
  "F5",
  "F6",
  "F7",
  "F8",
  "F9",
  "F10",
  "F11",
  "F12",
  // System/special keys that don't indicate page interaction
  "PrintScreen",
  "Pause",
  "Insert",
  "ContextMenu",
]);

/**
 * Check if a key press should trigger finalization
 * Returns true for interactive keys (Tab, Enter, Space, character input, arrows, etc.)
 * Returns false for modifier keys, function keys, and system keys
 */
function isInteractiveKey(key: string): boolean {
  return !IGNORED_KEYS.has(key);
}

/**
 * Handle user interaction (click or keydown) - finalize soft navigation if conditions are met
 * Only finalizes if we've been quiet for a minimum threshold
 */
function handleUserInteraction(interactionType: string): void {
  if (!activeNavigation || activeNavigation.reported) return;

  // Skip if page is hidden (user switched tabs)
  if (document.hidden) {
    debugLog(`${interactionType} ignored - page is hidden`);
    return;
  }

  // Only finalize if we've had some quiet time
  // This prevents premature finalization during active loading
  if (activeNavigation.quietStartTime !== null) {
    const quietDuration = performance.now() - activeNavigation.quietStartTime;
    if (quietDuration >= MIN_QUIET_FOR_USER_INTERACTION_MS) {
      debugLog(
        `User ${interactionType} detected after ${Math.round(
          quietDuration
        )}ms quiet, finalizing soft navigation...`
      );
      finalizeSoftNavigation();
    } else {
      debugLog(
        `User ${interactionType} ignored - only ${Math.round(
          quietDuration
        )}ms quiet (need ${MIN_QUIET_FOR_USER_INTERACTION_MS}ms)`
      );
    }
  } else {
    debugLog(`User ${interactionType} ignored - navigation not yet quiet`);
  }
}

/**
 * Handle user click - finalize soft navigation if conditions are met
 */
function handleUserClick(): void {
  handleUserInteraction("click");
}

/**
 * Handle user keydown - finalize soft navigation if conditions are met
 * Only interactive keys (not modifiers/function keys) trigger finalization
 */
function handleUserKeydown(e: KeyboardEvent): void {
  // Only handle interactive keys
  if (!isInteractiveKey(e.key)) {
    debugLog(`Keydown ignored - non-interactive key: ${e.key}`);
    return;
  }

  handleUserInteraction(`keydown (${e.key})`);
}

// ============================================================================
// Rating Functions
// ============================================================================

/**
 * ICP Rating (Interaction Contentful Paint - soft navigation's LCP equivalent)
 * Uses same thresholds as LCP for consistency
 */
function getICPRating(icp: number): "good" | "needs-improvement" | "poor" {
  if (icp <= 1500) return "good";
  if (icp <= 3000) return "needs-improvement";
  return "poor";
}

/**
 * CLS Rating for layout shift score
 * Based on Core Web Vitals thresholds
 */
function getCLSRating(cls: number): "good" | "needs-improvement" | "poor" {
  if (cls <= 0.1) return "good";
  if (cls <= 0.25) return "needs-improvement";
  return "poor";
}

// ============================================================================
// Resource Processing
// ============================================================================

/**
 * Extract timing info from a resource entry
 */
function extractNetworkRequestInfo(
  entry: ResourceTimingEntry,
  navStartTime: number
): SoftNavNetworkRequest {
  const startTimeRelative = Math.round(entry.startTime - navStartTime);
  const duration = Math.round(entry.duration);

  // TTFB is the most useful timing metric (only if timing info is available)
  const hasTiming = entry.requestStart > 0;

  return {
    url: entry.name,
    initiatorType: entry.initiatorType || "other",
    startTime: startTimeRelative,
    duration,
    transferSize: entry.transferSize || 0,
    ttfb: hasTiming
      ? Math.round(entry.responseStart - entry.requestStart)
      : undefined,
  };
}

/**
 * Process resource timing entries for active soft navigation
 */
function processResources(entries: ResourceTimingEntry[]): void {
  if (!activeNavigation || activeNavigation.reported) return;

  // Skip if page is hidden
  if (document.hidden) return;

  let hasNewActivity = false;

  for (const entry of entries) {
    // Skip ignored URLs
    if (shouldIgnoreForTTI(entry.name)) {
      continue;
    }

    // Skip resources that started well before our navigation
    // Use entryStartTime (from soft-navigation entry) for comparison since
    // resource timing entries use the same time base (relative to timeOrigin)
    if (entry.startTime < activeNavigation.entryStartTime - 500) {
      continue;
    }

    // Skip if we've already processed this URL (avoid duplicates from buffered + live)
    // Use responseEnd as part of key for more reliable deduplication
    const urlKey = `${entry.name}_${Math.round(entry.startTime)}_${Math.round(
      entry.responseEnd
    )}`;
    if (activeNavigation.processedUrls.has(urlKey)) {
      continue;
    }
    activeNavigation.processedUrls.add(urlKey);

    // Track the latest completion time
    if (entry.responseEnd > activeNavigation.lastResourceEndTime) {
      activeNavigation.lastResourceEndTime = entry.responseEnd;
      hasNewActivity = true;
    }

    // Collect detailed network request info
    const requestInfo = extractNetworkRequestInfo(
      entry,
      activeNavigation.entryStartTime
    );
    activeNavigation.networkRequests.push(requestInfo);
    activeNavigation.networkRequestCount++;

    debugLog(
      `Resource: ${entry.initiatorType} ${entry.name.substring(0, 60)}... ` +
        `duration=${requestInfo.duration}ms, size=${requestInfo.transferSize}B`
    );
  }

  if (hasNewActivity) {
    // Reset quiet state since we got new activity
    activeNavigation.quietStartTime = null;
    // Reset quiet timer since we got new activity
    scheduleQuietCheck();
  }
}

/**
 * Process long task entries for active soft navigation
 */
function processLongTasks(entries: LongTaskEntry[]): void {
  if (!activeNavigation || activeNavigation.reported) return;

  // Skip if page is hidden
  if (document.hidden) return;

  let hasNewActivity = false;

  for (const entry of entries) {
    // Skip tasks that started before our navigation
    if (entry.startTime < activeNavigation.entryStartTime - 100) {
      continue;
    }

    const taskEndTime = entry.startTime + entry.duration;

    // Track long task metrics
    activeNavigation.longTaskCount++;

    // Total Blocking Time: sum of (duration - 50ms) for all long tasks
    // Long tasks are defined as >50ms, so blocking time is the excess
    const blockingTime = Math.max(0, entry.duration - 50);
    activeNavigation.totalBlockingTime += blockingTime;

    // Track last long task end time
    if (taskEndTime > activeNavigation.lastLongTaskEndTime) {
      activeNavigation.lastLongTaskEndTime = taskEndTime;
      hasNewActivity = true;
    }

    debugLog(
      `Long Task: duration=${Math.round(
        entry.duration
      )}ms, blocking=${Math.round(blockingTime)}ms`
    );
  }

  if (hasNewActivity) {
    // Reset quiet state since we got new activity
    activeNavigation.quietStartTime = null;
    // Reset quiet timer
    scheduleQuietCheck();
  }
}

/**
 * Process layout shift entries for active soft navigation
 */
function processLayoutShifts(entries: LayoutShiftEntry[]): void {
  if (!activeNavigation || activeNavigation.reported) return;

  // Skip if page is hidden
  if (document.hidden) return;

  for (const entry of entries) {
    // Skip shifts caused by user input (these are expected)
    if (entry.hadRecentInput) {
      continue;
    }

    // Skip shifts that occurred before our navigation
    if (entry.startTime < activeNavigation.entryStartTime) {
      continue;
    }

    // Accumulate layout shift score
    activeNavigation.cumulativeLayoutShift += entry.value;
    activeNavigation.layoutShiftCount++;

    debugLog(`Layout Shift: value=${entry.value.toFixed(4)}`);
  }
}

/**
 * Schedule check for quiet period
 */
function scheduleQuietCheck(): void {
  if (!activeNavigation || activeNavigation.reported) return;

  if (activeNavigation.quietTimer) {
    clearTimeout(activeNavigation.quietTimer);
  }

  // Cancel any pending LCP wait timer when new activity comes in
  if (activeNavigation.lcpWaitTimer) {
    clearTimeout(activeNavigation.lcpWaitTimer);
    activeNavigation.lcpWaitTimer = null;
  }

  // Calculate time since last activity (considering both network and long tasks)
  const lastActivityTime = Math.max(
    activeNavigation.lastResourceEndTime,
    activeNavigation.lastLongTaskEndTime
  );
  const timeSinceLastActivity = performance.now() - lastActivityTime;
  const waitTime = Math.max(10, SOFT_NAV_QUIET_MS - timeSinceLastActivity);

  activeNavigation.quietTimer = setTimeout(checkQuiet, waitTime);
}

/**
 * Check if we've been quiet long enough
 */
function checkQuiet(): void {
  if (!activeNavigation || activeNavigation.reported) return;

  // Skip if page is hidden
  if (document.hidden) {
    // Re-schedule check for when page becomes visible
    scheduleQuietCheck();
    return;
  }

  const now = performance.now();

  // Consider both network and long task activity
  const lastActivityTime = Math.max(
    activeNavigation.lastResourceEndTime,
    activeNavigation.lastLongTaskEndTime
  );
  const timeSinceLastActivity = now - lastActivityTime;

  if (timeSinceLastActivity >= SOFT_NAV_QUIET_MS) {
    // Mark when quiet period started (for click finalization check)
    if (activeNavigation.quietStartTime === null) {
      activeNavigation.quietStartTime = now - timeSinceLastActivity;
    }

    // Network and JS quiet achieved - now wait for LCP if we haven't received it yet
    if (!activeNavigation.lcpReceived) {
      debugLog(
        `Quiet for ${Math.round(timeSinceLastActivity)}ms, waiting for LCP...`
      );
      // Start LCP wait timer if not already started
      if (!activeNavigation.lcpWaitTimer) {
        activeNavigation.lcpWaitTimer = setTimeout(() => {
          debugLog(
            `LCP wait timeout (${LCP_WAIT_MS}ms), finalizing without LCP...`
          );
          finalizeSoftNavigation();
        }, LCP_WAIT_MS);
      }
    } else {
      // We have LCP, finalize now
      debugLog(
        `Quiet for ${Math.round(
          timeSinceLastActivity
        )}ms and LCP received, finalizing...`
      );
      finalizeSoftNavigation();
    }
  } else {
    // Not quiet yet, check again
    scheduleQuietCheck();
  }
}

// ============================================================================
// ICP Processing (Interaction Contentful Paint)
// ============================================================================

/**
 * Process interaction-contentful-paint entries for soft navigation LCP
 * Chrome 139+ uses this instead of largest-contentful-paint for soft navigations
 */
function processICPEntry(entry: InteractionContentfulPaintEntry): void {
  if (!activeNavigation || activeNavigation.reported) return;

  // Check if this ICP belongs to our soft navigation via navigationId
  if (
    entry.navigationId &&
    entry.navigationId === activeNavigation.entry.navigationId
  ) {
    // Both entry.startTime and entryStartTime are relative to performance.timeOrigin
    activeNavigation.lcp = Math.round(
      entry.startTime - activeNavigation.entryStartTime
    );
    activeNavigation.lcpElement = getElementSelector(entry.element);
    activeNavigation.lcpReceived = true;

    debugLog(
      `ICP (soft nav LCP) for nav ${entry.navigationId}: ${activeNavigation.lcp}ms (${activeNavigation.lcpElement})`
    );

    // If we were waiting for LCP, cancel the wait timer and reschedule quiet check
    // Don't immediately finalize - there might still be network activity
    if (activeNavigation.lcpWaitTimer) {
      clearTimeout(activeNavigation.lcpWaitTimer);
      activeNavigation.lcpWaitTimer = null;
      debugLog("ICP received while waiting, checking quiet state...");
      // Check if we're still quiet - if so, finalize; otherwise wait for quiet
      checkQuiet();
    } else {
      // Reset quiet timer - ICP is significant activity
      scheduleQuietCheck();
    }
  }
}

/**
 * Get CSS selector for an element
 */
function getElementSelector(element?: Element): string | undefined {
  if (!element) return undefined;

  const tag = element.tagName?.toLowerCase() || "";
  const id = element.id ? `#${element.id}` : "";
  const className = element.className
    ? `.${element.className.toString().split(" ")[0]}`
    : "";

  return `${tag}${id}${className}` || undefined;
}

// ============================================================================
// Reporting
// ============================================================================

/**
 * Finalize and report soft navigation
 */
function finalizeSoftNavigation(): void {
  if (!activeNavigation || activeNavigation.reported) return;
  activeNavigation.reported = true;

  // Calculate duration using the correct time bases:
  // - entryStartTime, lastResourceEndTime, lastLongTaskEndTime are all relative to performance.timeOrigin
  // - lcp is already relative to entryStartTime
  // Duration should be the max of: network activity time, long task time, and LCP time

  const networkDuration =
    activeNavigation.lastResourceEndTime > activeNavigation.entryStartTime
      ? activeNavigation.lastResourceEndTime - activeNavigation.entryStartTime
      : 0;

  const longTaskDuration =
    activeNavigation.lastLongTaskEndTime > activeNavigation.entryStartTime
      ? activeNavigation.lastLongTaskEndTime - activeNavigation.entryStartTime
      : 0;

  const lcpDuration = activeNavigation.lcp ?? 0;

  // Duration is the maximum of all activity types
  let duration = Math.round(
    Math.max(networkDuration, longTaskDuration, lcpDuration)
  );

  if (duration === 0) {
    duration = Math.round(performance.now() - activeNavigation.entryStartTime);
  }

  debugLog(
    `Duration calculation:`,
    `\n  entryStartTime: ${Math.round(activeNavigation.entryStartTime)}`,
    `\n  networkDuration: ${Math.round(networkDuration)}ms`,
    `\n  longTaskDuration: ${Math.round(longTaskDuration)}ms`,
    `\n  lcpDuration: ${lcpDuration}ms`,
    `\n  final duration: ${duration}ms`
  );

  // Sort network requests by start time for readability
  const sortedNetworkRequests = [...activeNavigation.networkRequests].sort(
    (a, b) => a.startTime - b.startTime
  );

  const reportData: SoftNavigationReportData = {
    type: "softnavigation",
    navigationId: activeNavigation.entry.navigationId,
    url: activeNavigation.entry.name,
    fromRoute: activeNavigation.fromRoute,
    toRoute: activeNavigation.entry.name,
    startTime: Math.round(activeNavigation.entryStartTime),
    duration,
    // ICP data (Interaction Contentful Paint - soft navigation's equivalent of LCP)
    icp: activeNavigation.lcp,
    icpElement: activeNavigation.lcpElement,
    icpRating: activeNavigation.lcp
      ? getICPRating(activeNavigation.lcp)
      : undefined,
    // Network activity summary
    networkRequestCount: activeNavigation.networkRequestCount,
    lastNetworkActivityTime: Math.round(activeNavigation.lastResourceEndTime),
    // Detailed network requests during this navigation
    networkRequests: sortedNetworkRequests,
    // Long task metrics
    longTaskCount: activeNavigation.longTaskCount,
    totalBlockingTime: Math.round(activeNavigation.totalBlockingTime),
    lastLongTaskEndTime: Math.round(activeNavigation.lastLongTaskEndTime),
    // Layout shift metrics
    cumulativeLayoutShift:
      Math.round(activeNavigation.cumulativeLayoutShift * 10000) / 10000,
    layoutShiftCount: activeNavigation.layoutShiftCount,
    clsRating:
      activeNavigation.layoutShiftCount > 0
        ? getCLSRating(activeNavigation.cumulativeLayoutShift)
        : undefined,
    effectiveNetworkType: getEffectiveNetworkType(),
    sessionId: getSessionId(),
    userId: getUserId(),
    locationId: getLocationId(),
    timestamp: new Date().toISOString(),
  };

  debugLog(
    `Soft Navigation Complete: ${activeNavigation.fromRoute} -> ${activeNavigation.entry.name}`,
    `\n  Duration: ${duration}ms`,
    `\n  ICP: ${activeNavigation.lcp ?? "N/A"}ms`,
    `\n  Network requests: ${activeNavigation.networkRequestCount}`,
    `\n  Long tasks: ${activeNavigation.longTaskCount} (TBT: ${Math.round(
      activeNavigation.totalBlockingTime
    )}ms)`,
    `\n  CLS: ${activeNavigation.cumulativeLayoutShift.toFixed(4)}`
  );

  PerformanceMonitoringService.logPageLoadPerformance(reportData);

  // Cleanup timers
  cleanupActiveNavigation();
}

/**
 * Cleanup active navigation state
 */
function cleanupActiveNavigation(): void {
  if (activeNavigation) {
    if (activeNavigation.quietTimer) {
      clearTimeout(activeNavigation.quietTimer);
    }
    if (activeNavigation.lcpWaitTimer) {
      clearTimeout(activeNavigation.lcpWaitTimer);
    }
    if (activeNavigation.maxTimer) {
      clearTimeout(activeNavigation.maxTimer);
    }
    activeNavigation = null;
  }
}

// ============================================================================
// Soft Navigation Handler
// ============================================================================

/**
 * Handle new soft navigation entry
 */
function handleSoftNavigation(entry: SoftNavigationEntry): void {
  // Skip if page is hidden
  if (document.hidden) {
    debugLog("Soft navigation ignored - page is hidden");
    return;
  }

  // Cancel any previous navigation that hasn't completed
  // Don't finalize - the user abandoned it by navigating away
  if (activeNavigation && !activeNavigation.reported) {
    debugLog("New soft navigation detected, canceling previous (abandoned)...");
    cleanupActiveNavigation();
  }

  const now = performance.now();

  // Capture fromRoute before updating lastUrl
  const fromRoute = lastUrl;

  debugLog(
    `Soft Navigation Started: ${entry.name}`,
    `\n  navigationId: ${entry.navigationId}`,
    `\n  fromRoute: ${fromRoute}`,
    `\n  entry.startTime: ${Math.round(entry.startTime)}`,
    `\n  performance.now(): ${Math.round(now)}`
  );

  // Update lastUrl for next navigation
  lastUrl = entry.name;

  // Start tracking new navigation
  activeNavigation = {
    entry,
    fromRoute,
    entryStartTime: entry.startTime, // From the soft-navigation entry (relative to timeOrigin)
    actualStartTime: now, // When we started tracking (performance.now())
    lastResourceEndTime: entry.startTime, // Initialize to entry start time
    networkRequestCount: 0,
    networkRequests: [], // Detailed network request info
    processedUrls: new Set<string>(), // Track processed URLs to avoid duplicates
    lcpReceived: false,
    // Long task tracking
    longTaskCount: 0,
    totalBlockingTime: 0,
    lastLongTaskEndTime: entry.startTime,
    // Layout shift tracking
    cumulativeLayoutShift: 0,
    layoutShiftCount: 0,
    // Timers
    quietTimer: null,
    lcpWaitTimer: null,
    maxTimer: null,
    reported: false,
    quietStartTime: null,
  };

  // Check for any buffered resources that may have already loaded
  // This catches resources that started loading before the soft-navigation entry fired
  processBufferedResources();

  // Schedule initial quiet check
  scheduleQuietCheck();

  // Max timeout failsafe
  activeNavigation.maxTimer = setTimeout(() => {
    debugLog("Max timeout reached for soft navigation, finalizing...");
    finalizeSoftNavigation();
  }, MAX_SOFT_NAV_WAIT_MS);
}

/**
 * Process any buffered resource entries that loaded during/after navigation start
 */
function processBufferedResources(): void {
  if (!activeNavigation) return;

  try {
    // Get all resource entries from the performance buffer
    const entries = performance.getEntriesByType(
      "resource"
    ) as ResourceTimingEntry[];

    // Filter to resources that started around our navigation time
    // Use entryStartTime since both are relative to performance.timeOrigin
    const relevantEntries = entries.filter((entry) => {
      // Include resources that started within 500ms before navigation or after
      return entry.startTime >= activeNavigation!.entryStartTime - 500;
    });

    if (relevantEntries.length > 0) {
      debugLog(
        `Processing ${relevantEntries.length} buffered resources for soft navigation`
      );
      processResources(relevantEntries);
    } else {
      debugLog(
        `No buffered resources found after startTime ${Math.round(
          activeNavigation.entryStartTime
        )}`
      );
    }
  } catch (error) {
    debugLog("Error processing buffered resources:", error);
  }
}

/**
 * Handle hard navigation events to keep lastUrl in sync
 */
function handleHardNavigation(): void {
  lastUrl = window.location.href;
  debugLog("Hard navigation detected, updated lastUrl:", lastUrl);
}

// ============================================================================
// Public API
// ============================================================================

/**
 * Initialize soft navigation tracking
 * Sets up observers for soft navigation, ICP, network resources, long tasks, and layout shifts
 *
 * Chrome 139+ exposes:
 * - "interaction-contentful-paint" (ICP) for soft navigation LCP
 *   (NOT "largest-contentful-paint" which is only for hard navigations)
 *
 * Note: FCP is NOT tracked for soft navigations because it's a one-time
 * metric that only fires during initial page load.
 */
export function initSoftNavigationTracking(): boolean {
  if (initialized) {
    debugLog("Already initialized");
    return true;
  }

  if (!isSoftNavigationSupported()) {
    debugLog("Soft Navigation API not supported - skipping initialization");
    return false;
  }

  // 1. Soft Navigation observer - detects navigation start
  softNavObserver = createSoftNavigationObserver((entry) => {
    handleSoftNavigation(entry);
  });

  if (!softNavObserver) {
    return false;
  }

  // 2. Interaction Contentful Paint observer - THIS IS THE CORRECT ONE FOR SOFT NAV LCP
  // Chrome 139+ uses "interaction-contentful-paint" instead of "largest-contentful-paint"
  // for measuring LCP after soft navigations
  try {
    // Check if interaction-contentful-paint is supported
    if (
      PerformanceObserver.supportedEntryTypes?.includes(
        "interaction-contentful-paint"
      )
    ) {
      icpObserver = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          processICPEntry(entry as InteractionContentfulPaintEntry);
        }
      });
      icpObserver.observe({
        type: "interaction-contentful-paint",
        buffered: true,
      });
      debugLog(
        "ICP observer started - this is the correct metric for soft navigation LCP"
      );
    } else {
      debugLog(
        "interaction-contentful-paint not supported - soft nav LCP will not be available"
      );
    }
  } catch (error) {
    debugLog("Failed to create ICP observer:", error);
  }

  // 3. Long Task observer - tracks JavaScript blocking time
  try {
    if (PerformanceObserver.supportedEntryTypes?.includes("longtask")) {
      longTaskObserver = new PerformanceObserver((list) => {
        processLongTasks(list.getEntries() as LongTaskEntry[]);
      });
      longTaskObserver.observe({ type: "longtask", buffered: true });
      debugLog("Long Task observer started for soft navigation TBT");
    } else {
      debugLog("longtask not supported - TBT will not be tracked");
    }
  } catch (error) {
    debugLog("Failed to create Long Task observer:", error);
  }

  // 4. Layout Shift observer - tracks visual stability
  try {
    if (PerformanceObserver.supportedEntryTypes?.includes("layout-shift")) {
      layoutShiftObserver = new PerformanceObserver((list) => {
        processLayoutShifts(list.getEntries() as LayoutShiftEntry[]);
      });
      layoutShiftObserver.observe({ type: "layout-shift", buffered: true });
      debugLog("Layout Shift observer started for soft navigation CLS");
    } else {
      debugLog("layout-shift not supported - CLS will not be tracked");
    }
  } catch (error) {
    debugLog("Failed to create Layout Shift observer:", error);
  }

  // 5. Resource observer - tracks network activity for duration calculation
  resourceObserver = createResourceObserver(processResources);
  if (resourceObserver) {
    debugLog("Resource observer started for soft navigation duration");
  }

  // 6. User interaction listeners - finalize soft navigation on click or keydown (with conditions)
  clickListener = handleUserClick;
  document.addEventListener("click", clickListener, { capture: true });

  keydownListener = handleUserKeydown;
  document.addEventListener("keydown", keydownListener, { capture: true });

  debugLog(
    "Click and keydown listeners added to finalize soft navigation on user interaction"
  );

  // 7. Hard navigation listeners - keep lastUrl in sync
  popstateListener = handleHardNavigation;
  window.addEventListener("popstate", popstateListener);

  pageshowListener = (e: PageTransitionEvent) => {
    if (e.persisted) {
      // Page was restored from bfcache
      handleHardNavigation();
    }
  };
  window.addEventListener("pageshow", pageshowListener);

  initialized = true;
  debugLog(
    "Soft Navigation tracking initialized with ICP + duration + TBT + CLS tracking"
  );
  return true;
}

/**
 * Stop soft navigation tracking and cleanup
 */
export function stopSoftNavigationTracking(): void {
  // Finalize any active navigation before stopping
  if (activeNavigation && !activeNavigation.reported) {
    finalizeSoftNavigation();
  }

  // Disconnect all observers
  if (softNavObserver) {
    softNavObserver.disconnect();
    softNavObserver = null;
  }
  if (icpObserver) {
    icpObserver.disconnect();
    icpObserver = null;
  }
  if (longTaskObserver) {
    longTaskObserver.disconnect();
    longTaskObserver = null;
  }
  if (layoutShiftObserver) {
    layoutShiftObserver.disconnect();
    layoutShiftObserver = null;
  }
  if (resourceObserver) {
    resourceObserver.disconnect();
    resourceObserver = null;
  }

  // Remove user interaction listeners
  if (clickListener) {
    document.removeEventListener("click", clickListener, { capture: true });
    clickListener = null;
  }
  if (keydownListener) {
    document.removeEventListener("keydown", keydownListener, { capture: true });
    keydownListener = null;
  }

  // Remove hard navigation listeners
  if (popstateListener) {
    window.removeEventListener("popstate", popstateListener);
    popstateListener = null;
  }
  if (pageshowListener) {
    window.removeEventListener("pageshow", pageshowListener);
    pageshowListener = null;
  }

  cleanupActiveNavigation();
  initialized = false;
  debugLog("Soft Navigation tracking stopped");
}

/**
 * Check if soft navigation tracking is currently active
 */
export function isSoftNavigationTrackingActive(): boolean {
  return initialized && softNavObserver !== null;
}

/**
 * Get the current soft navigation ID if a soft navigation is in progress
 * Returns undefined if no soft navigation is active or tracking is not initialized
 *
 * This can be used by resource monitoring to correlate resources with soft navigations
 */
export function getCurrentSoftNavigationId(): number | undefined {
  if (!activeNavigation || activeNavigation.reported) {
    return undefined;
  }
  return activeNavigation.entry.navigationId;
}
